import { useState, useMemo } from 'react';
import type { EstimateItem } from '../../shared/types/estimate.types';

interface EstimatesTableProps {
  items: EstimateItem[];
  isLoading?: boolean;
  canEdit?: boolean;
  onRowClick: (id: string) => void;
  onEdit?: (item: EstimateItem) => void;
  onDelete?: (item: EstimateItem) => void;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatNumber(value: number) {
  return value.toLocaleString('vi-VN', { maximumFractionDigits: 4 });
}

export function EstimatesTable({ items, isLoading, canEdit, onRowClick, onEdit, onDelete }: EstimatesTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(items.map((i) => i.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const flattenedTree = useMemo(() => {
    const map = new Map<string, EstimateItem & { children: any[] }>();
    const roots: any[] = [];

    items.forEach((item) => {
      map.set(item.id, { ...item, children: [] });
    });

    items.forEach((item) => {
      const node = map.get(item.id)!;
      if (item.parent_id && map.has(item.parent_id)) {
        map.get(item.parent_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    const result: Array<{ item: EstimateItem; level: number; hasChildren: boolean; isExpanded: boolean; isVisible: boolean }> = [];

    const traverse = (node: any, level: number, parentVisible: boolean) => {
      const isExpanded = expandedIds.has(node.id);
      const isVisible = parentVisible;

      result.push({
        item: node,
        level,
        hasChildren: node.children.length > 0,
        isExpanded,
        isVisible,
      });

      node.children.forEach((child: any) => traverse(child, level + 1, parentVisible && isExpanded));
    };

    roots.forEach((root) => traverse(root, 0, true));
    return result;
  }, [items, expandedIds]);

  return (
    <div className="h-full w-full overflow-auto">
      <table className="w-full min-w-[1280px] border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-slate-100 shadow-sm">
          <tr>
            <th className="w-12 border-b border-r border-slate-200 p-3 text-center text-xs font-medium text-slate-700" rowSpan={2}>STT</th>
            <th className="w-28 border-b border-r border-slate-200 p-3 text-xs font-medium text-slate-700" rowSpan={2}>Mã</th>
            <th className="border-b border-r border-slate-200 p-3 text-xs font-medium text-slate-700" rowSpan={2}>Hạng mục / Công việc</th>
            <th className="w-20 border-b border-r border-slate-200 p-3 text-center text-xs font-medium text-slate-700" rowSpan={2}>ĐVT</th>
            <th className="w-28 border-b border-r border-slate-200 bg-blue-50/50 p-3 text-center text-xs font-medium text-slate-700" rowSpan={2}>Khối lượng</th>
            <th className="w-36 border-b border-r border-slate-200 bg-blue-50 p-3 text-right text-xs font-medium text-slate-800" rowSpan={2}>Đơn giá</th>
            <th className="border-b border-r border-slate-200 bg-amber-50/30 p-2 text-center text-[11px] font-medium text-slate-700" colSpan={4}>Cơ cấu chi phí</th>
            <th className="w-40 border-b border-slate-200 p-3 text-right text-xs font-medium text-slate-800" rowSpan={2}>Thành tiền</th>
            {canEdit && <th className="w-24 border-b border-slate-200 p-3 text-right text-xs font-medium text-slate-700" rowSpan={2}>Thao tác</th>}
          </tr>
          <tr>
            <th className="w-28 border-b border-r border-slate-200 bg-emerald-50/30 p-2 text-right text-[10px] font-medium text-slate-600">Vật tư</th>
            <th className="w-28 border-b border-r border-slate-200 bg-indigo-50/30 p-2 text-right text-[10px] font-medium text-slate-600">Nhân công</th>
            <th className="w-28 border-b border-r border-slate-200 bg-orange-50/30 p-2 text-right text-[10px] font-medium text-slate-600">Thiết bị</th>
            <th className="w-28 border-b border-r border-slate-200 bg-slate-50 p-2 text-right text-[10px] font-medium text-slate-600">Thầu phụ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {isLoading ? (
            <tr>
              <td colSpan={canEdit ? 12 : 11} className="p-8 text-center text-sm text-slate-500">Đang tải hạng mục dự toán...</td>
            </tr>
          ) : flattenedTree.length === 0 ? (
            <tr>
              <td colSpan={canEdit ? 12 : 11} className="p-8 text-center text-sm text-slate-500">Chưa có hạng mục bóc tách cho dự toán này.</td>
            </tr>
          ) : (
            flattenedTree.map(({ item: row, level, hasChildren, isExpanded, isVisible }, index) => {
              if (!isVisible) return null;
              const isFolder = row.category_code === 'folder' || !row.unit || hasChildren;
              return (
                <tr
                  key={row.id}
                  className={`cursor-pointer transition-colors hover:bg-blue-50/30 ${isFolder ? 'bg-slate-50/80' : ''}`}
                  onClick={() => onRowClick(row.id)}
                >
                  <td className="border-r border-slate-100 p-2 text-center text-xs text-slate-500">{index + 1}</td>
                  <td className="border-r border-slate-100 p-2 text-[11px] font-medium text-blue-600">
                    <span className="rounded border border-blue-100 bg-blue-50 px-1.5 py-0.5">{row.item_code || '-'}</span>
                  </td>
                  <td className="border-r border-slate-100 p-2 text-xs text-slate-700 sm:text-sm">
                    <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 1.5}rem` }}>
                      {hasChildren ? (
                        <button onClick={(e) => toggleExpand(row.id, e)} className="flex h-5 w-5 items-center justify-center rounded hover:bg-slate-200">
                          <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-slate-500`} />
                        </button>
                      ) : (
                        <span className="w-5" />
                      )}
                      {isFolder && <i className="ri-folder-open-fill text-emerald-600" />}
                      <span className={isFolder ? 'font-medium text-slate-900' : 'font-normal'}>{row.item_name}</span>
                      {(row.unit_price <= 0 || row.quantity <= 0) && !isFolder && (
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rose-100 text-rose-600" title="Thiếu khối lượng hoặc đơn giá">
                          <i className="ri-alert-fill text-[10px]" />
                        </span>
                      )}
                    </div>
                    {(row.specification || row.species || row.zone?.name) && (
                      <div className="mt-0.5 text-[10px] text-slate-400">
                        {[row.specification, row.species, row.zone?.name].filter(Boolean).join(' | ')}
                      </div>
                    )}
                  </td>
                  <td className="border-r border-slate-100 p-2 text-center text-xs text-slate-600">{row.unit || '-'}</td>
                  <td className="border-r border-slate-100 bg-blue-50/10 p-2 text-center text-xs font-medium text-slate-700">{formatNumber(row.quantity)}</td>
                  <td className="border-r border-slate-200 bg-blue-50/30 p-2 text-right text-xs font-medium text-slate-800">{formatCurrency(row.unit_price)}</td>
                  <td className="border-r border-slate-100 bg-emerald-50/10 p-2 text-right text-xs text-emerald-700">{formatCurrency(row.material_cost)}</td>
                  <td className="border-r border-slate-100 bg-indigo-50/10 p-2 text-right text-xs text-indigo-700">{formatCurrency(row.labor_cost)}</td>
                  <td className="border-r border-slate-200 bg-orange-50/10 p-2 text-right text-xs text-orange-700">{formatCurrency(row.equipment_cost)}</td>
                  <td className="border-r border-slate-200 bg-slate-50 p-2 text-right text-xs text-slate-700">{formatCurrency(row.subcontract_cost)}</td>
                  <td className="p-2 text-right text-xs font-medium text-slate-900 sm:text-sm">{formatCurrency(row.total_amount)}</td>
                  {canEdit && (
                    <td className="p-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onEdit?.(row);
                          }}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                          title="Sửa hạng mục"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDelete?.(row);
                          }}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                          title="Xóa hạng mục"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
